import React from 'react';
import { useAuth } from '../context/AuthContext';
import { useRole } from '../lib/permissions';
import { useOverviewGlobal } from '../hooks/useOverviewGlobal';
import { Building2, ShieldCheck, User, Wrench, Users } from 'lucide-react';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from './ui/tooltip';

/**
 * TenantChip — always-visible sidebar indicator showing the caller's
 * active workspace and effective role.
 *
 * Styling reserves green (`emerald`) for true success states (Filed /
 * Submitted / Connected). "Active/primary" selection uses a blue-slate
 * palette so it does NOT collide with success semantics.
 *
 * Layout contract (iter175 overlap fix):
 *   - `flex w-full min-w-0` block — never overflows its container
 *   - Workspace name: `flex-1 min-w-0 truncate` (single line, ellipsis)
 *   - Role chip: `flex-shrink-0 whitespace-nowrap`, compact label
 *   - Role label falls back to a lucide icon-only tooltip when tight
 */

const ROLE_META = {
  // Meridian internal (support) — blue accent
  system_admin: {
    label: 'System Admin', short: 'Sys Admin', icon: ShieldCheck,
    tone: 'meridian',
  },
  meridian_operator: {
    label: 'Meridian Operator', short: 'Operator', icon: Wrench,
    tone: 'meridian',
  },
  meridian_approver: {
    label: 'Meridian Approver', short: 'Approver', icon: ShieldCheck,
    tone: 'meridian',
  },
  // Merchant Admin — primary/active (subscription owner)
  merchant_admin: {
    label: 'Merchant Admin', short: 'Admin', icon: ShieldCheck,
    tone: 'primary',
  },
  // Merchant staff — neutral slate
  merchant_operator: {
    label: 'Merchant Operator', short: 'Operator', icon: User,
    tone: 'neutral',
  },
  merchant_viewer: {
    label: 'Merchant Viewer', short: 'Viewer', icon: User,
    tone: 'neutral',
  },
  // Broker collaborator — amber (external, scoped)
  broker_collaborator: {
    label: 'Broker', short: 'Broker', icon: Users, tone: 'broker',
  },
  '3pl_operator': {
    label: '3PL Operator', short: '3PL', icon: Wrench, tone: 'neutral',
  },
  // Iter263 Phase R3-3 — partner roles: primary tone for the admin
  // (writes across workspaces), neutral for the viewer.
  partner_admin: {
    label: 'Partner Admin', short: 'Partner', icon: ShieldCheck,
    tone: 'primary',
  },
  partner_viewer: {
    label: 'Partner Viewer', short: 'Partner', icon: User,
    tone: 'neutral',
  },
};

const TONE_CLASSES = {
  primary:   { ring: 'ring-blue-200',    bg: 'bg-blue-50',    text: 'text-blue-800',    chipBg: 'bg-blue-100',    chipText: 'text-blue-800' },
  meridian:  { ring: 'ring-sky-200',     bg: 'bg-sky-50',     text: 'text-sky-800',     chipBg: 'bg-sky-100',     chipText: 'text-sky-800' },
  neutral:   { ring: 'ring-slate-200',   bg: 'bg-slate-50',   text: 'text-slate-800',   chipBg: 'bg-slate-100',   chipText: 'text-slate-700' },
  broker:    { ring: 'ring-amber-200',   bg: 'bg-amber-50',   text: 'text-amber-900',   chipBg: 'bg-amber-100',   chipText: 'text-amber-800' },
  crossmode: { ring: 'ring-red-200',     bg: 'bg-red-50',     text: 'text-red-800',     chipBg: 'bg-red-100',     chipText: 'text-red-800' },
};

export const TenantChip = () => {
  const { activeWorkspace, isCrossWorkspaceMode,
            isPartnerCrossMode } = useAuth();
  const role = useRole();
  const overviewGlobal = useOverviewGlobal();

  const canonical = role?.canonical || role?.raw;
  const meta = ROLE_META[canonical] || ROLE_META.merchant_viewer;
  const Icon = meta.icon;

  const workspaceLabel = overviewGlobal
    ? 'Global view'
    : (isCrossWorkspaceMode || isPartnerCrossMode)
    ? 'All workspaces'
    : (activeWorkspace?.name || activeWorkspace?.id?.slice(0, 8) || 'No workspace');

  const tone = (overviewGlobal || isCrossWorkspaceMode || isPartnerCrossMode)
    ? TONE_CLASSES.crossmode
    : TONE_CLASSES[meta.tone];

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className={`flex w-full min-w-0 items-center gap-1.5 pl-2 pr-1.5 py-1 rounded-md ring-1 ${tone.ring} ${tone.bg} ${tone.text} text-xs font-medium h-7`}
            data-testid="tenant-chip"
          >
            <Building2 className="h-3.5 w-3.5 opacity-70 flex-shrink-0" />
            <span
              className="flex-1 min-w-0 truncate"
              data-testid="tenant-chip-workspace"
              style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
            >
              {workspaceLabel}
            </span>
            <span
              className={`flex-shrink-0 whitespace-nowrap inline-flex items-center gap-1 rounded px-1.5 py-0.5 ${tone.chipBg} ${tone.chipText} text-[10px] font-semibold uppercase tracking-wide`}
              data-testid="tenant-chip-role"
            >
              <Icon className="h-3 w-3" />
              <span className="hidden xl:inline">{meta.short}</span>
            </span>
          </div>
        </TooltipTrigger>
        <TooltipContent side="right" className="text-xs">
          <div><strong>Workspace:</strong> {workspaceLabel}</div>
          <div><strong>Role:</strong> {meta.label}</div>
          {isCrossWorkspaceMode && (
            <div className="text-red-500"><strong>Cross-workspace mode</strong></div>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default TenantChip;
